/**
 * OnCallScheduleView — Frente N N3b: gestão da ESCALA de plantão do operador.
 *
 * CRUD de `OperatorOnCallShift` (operador × vigência) consumindo os endpoints de
 * N3a-2 (`/v1/operator-on-call-shifts`). Ativa o regime 3: quem está de plantão
 * mantém os alertas `urgent` chegando fora do expediente (a política define o
 * expediente — vide aba "Notificações do operador" em Configurações do tenant).
 *
 * Escrita gated `operator-oncall-schedule-manage` (`canManage`); leitura `-read`.
 * PATCH/DELETE usam If-Match (a `version` do shift · ADR-071).
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useOnCallShiftActions } from '../../hooks/use-operator-on-call';
import { useUsers } from '../../hooks/use-users';

import type { OperatorOnCallShiftWire } from '../../lib/api-client';
import type { JSX } from 'react';

export interface OnCallScheduleViewProps {
  readonly shifts: readonly OperatorOnCallShiftWire[];
  readonly canManage: boolean;
}

/** ISO → valor de `<input type="datetime-local">` no fuso do navegador (YYYY-MM-DDTHH:MM). */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

/** Valor de datetime-local → ISO (interpretado no fuso do navegador). `''`/inválido ⇒ null. */
function localInputToIso(local: string): string | null {
  if (local === '') return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatRange(startsAt: string, endsAt: string): string {
  const fmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const s = new Date(startsAt);
  const e = new Date(endsAt);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '—';
  return `${fmt.format(s)} → ${fmt.format(e)}`;
}

function isActiveNow(startsAt: string, endsAt: string): boolean {
  const now = Date.now();
  return new Date(startsAt).getTime() <= now && now < new Date(endsAt).getTime();
}

interface DraftShift {
  userId: string;
  startsAt: string; // datetime-local
  endsAt: string; // datetime-local
  note: string;
}

const EMPTY_DRAFT: DraftShift = { userId: '', startsAt: '', endsAt: '', note: '' };

export function OnCallScheduleView({ shifts, canManage }: OnCallScheduleViewProps): JSX.Element {
  const actions = useOnCallShiftActions();
  const usersQuery = useUsers();
  const users = useMemo(
    () => (usersQuery.data?.data ?? []).filter((u) => u.status === 'active'),
    [usersQuery.data],
  );
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersQuery.data?.data ?? []) m.set(u.id, u.name);
    return m;
  }, [usersQuery.data]);

  const [draft, setDraft] = useState<DraftShift>(EMPTY_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{ startsAt: string; endsAt: string; note: string }>({
    startsAt: '',
    endsAt: '',
    note: '',
  });

  const sorted = useMemo(
    () => [...shifts].sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [shifts],
  );

  const submitCreate = (): void => {
    if (draft.userId === '') {
      setCreateError('Escolha o operador.');
      return;
    }
    const startsAt = localInputToIso(draft.startsAt);
    const endsAt = localInputToIso(draft.endsAt);
    if (startsAt === null || endsAt === null) {
      setCreateError('Preencha início e fim.');
      return;
    }
    if (startsAt >= endsAt) {
      setCreateError('O fim deve ser depois do início.');
      return;
    }
    setCreateError(null);
    actions.create.mutate(
      {
        userId: draft.userId,
        startsAt,
        endsAt,
        ...(draft.note.trim() !== '' && { note: draft.note.trim() }),
      },
      { onSuccess: () => setDraft(EMPTY_DRAFT) },
    );
  };

  const startEdit = (shift: OperatorOnCallShiftWire): void => {
    setEditingId(shift.id);
    setEditError(null);
    setEditDraft({
      startsAt: isoToLocalInput(shift.startsAt),
      endsAt: isoToLocalInput(shift.endsAt),
      note: shift.note ?? '',
    });
  };

  const submitEdit = (shift: OperatorOnCallShiftWire): void => {
    const startsAt = localInputToIso(editDraft.startsAt);
    const endsAt = localInputToIso(editDraft.endsAt);
    if (startsAt === null || endsAt === null || startsAt >= endsAt) {
      setEditError('Preencha início e fim, com o fim depois do início.');
      return;
    }
    setEditError(null);
    actions.update.mutate(
      {
        id: shift.id,
        version: shift.version,
        startsAt,
        endsAt,
        note: editDraft.note.trim() === '' ? null : editDraft.note.trim(),
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const removeShift = (shift: OperatorOnCallShiftWire): void => {
    if (!window.confirm('Remover este plantão da escala?')) return;
    actions.remove.mutate({ id: shift.id, version: shift.version });
  };

  return (
    <div className="max-w-3xl">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Escala de plantão</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Quem está de plantão continua recebendo os alertas <strong>urgentes</strong> no celular
          fora do expediente. Os demais avisos seguem silenciados (a informação fica íntegra ao
          abrir o app). O expediente e o silenciamento são definidos em{' '}
          <Link to="/admin/config" className="text-accent hover:underline">
            Configurações › Notificações do operador
          </Link>
          .
        </p>
      </header>

      {canManage && (
        <div className="mb-6 rounded-xl border border-border-subtle bg-surface p-4">
          <h3 className="mb-3 text-sm font-medium text-text-primary">Escalar operador</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-text-secondary">
              <span className="mb-1 block">Operador</span>
              <select
                aria-label="Operador de plantão"
                className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
                value={draft.userId}
                disabled={actions.create.isPending}
                onChange={(e) => setDraft((d) => ({ ...d, userId: e.target.value }))}
              >
                <option value="">Selecione…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              {!usersQuery.isLoading && users.length === 0 && (
                <span className="mt-1 block text-[11px] text-amber-600">
                  Nenhum operador disponível para listar. A escala precisa da permissão de ver
                  usuários (users-read).
                </span>
              )}
            </label>
            <label className="text-xs text-text-secondary">
              <span className="mb-1 block">Observação (opcional)</span>
              <input
                type="text"
                aria-label="Observação do plantão"
                maxLength={500}
                className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
                value={draft.note}
                disabled={actions.create.isPending}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
              />
            </label>
            <label className="text-xs text-text-secondary">
              <span className="mb-1 block">Início (no seu fuso)</span>
              <input
                type="datetime-local"
                aria-label="Início do plantão"
                className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
                value={draft.startsAt}
                disabled={actions.create.isPending}
                onChange={(e) => setDraft((d) => ({ ...d, startsAt: e.target.value }))}
              />
            </label>
            <label className="text-xs text-text-secondary">
              <span className="mb-1 block">Fim (no seu fuso)</span>
              <input
                type="datetime-local"
                aria-label="Fim do plantão"
                className="w-full rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm"
                value={draft.endsAt}
                disabled={actions.create.isPending}
                onChange={(e) => setDraft((d) => ({ ...d, endsAt: e.target.value }))}
              />
            </label>
          </div>
          {createError !== null && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              {createError}
            </p>
          )}
          {actions.create.isError && (
            <p role="alert" className="mt-2 text-xs text-red-600">
              Não foi possível escalar. Tente novamente.
            </p>
          )}
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={submitCreate}
              disabled={actions.create.isPending}
              className="rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {actions.create.isPending ? 'Escalando…' : 'Escalar'}
            </button>
          </div>
        </div>
      )}

      <h3 className="mb-2 text-sm font-medium text-text-primary">
        Plantões escalados{' '}
        {sorted.length > 0 && <span className="text-text-secondary">({sorted.length})</span>}
      </h3>
      {actions.remove.isError && (
        <p role="alert" className="mb-2 text-xs text-red-600">
          Não foi possível remover (o plantão pode ter sido alterado por outra pessoa — recarregue).
        </p>
      )}
      {sorted.length === 0 ? (
        <p className="rounded-lg border border-border-subtle bg-app-bg px-4 py-6 text-center text-sm text-text-secondary">
          Nenhum plantão escalado.
        </p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((shift) => {
            const active = isActiveNow(shift.startsAt, shift.endsAt);
            const operatorName = nameById.get(shift.userId) ?? shift.userId;
            const isEditing = editingId === shift.id;
            return (
              <li
                key={shift.id}
                className={`rounded-lg border px-4 py-3 ${
                  active ? 'border-emerald-300 bg-emerald-50' : 'border-border-subtle bg-surface'
                }`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-text-primary">{operatorName}</p>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="datetime-local"
                        aria-label="Editar início do plantão"
                        className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm"
                        value={editDraft.startsAt}
                        onChange={(e) => setEditDraft((d) => ({ ...d, startsAt: e.target.value }))}
                      />
                      <input
                        type="datetime-local"
                        aria-label="Editar fim do plantão"
                        className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm"
                        value={editDraft.endsAt}
                        onChange={(e) => setEditDraft((d) => ({ ...d, endsAt: e.target.value }))}
                      />
                      <input
                        type="text"
                        aria-label="Editar observação do plantão"
                        maxLength={500}
                        placeholder="Observação"
                        className="flex-1 rounded border border-border-subtle bg-surface px-2 py-1 text-sm"
                        value={editDraft.note}
                        onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                      />
                    </div>
                    {editError !== null && (
                      <p role="alert" className="text-xs text-red-600">
                        {editError}
                      </p>
                    )}
                    {actions.update.isError && (
                      <p role="alert" className="text-xs text-red-600">
                        Não foi possível salvar (o plantão pode ter sido alterado por outra pessoa —
                        recarregue).
                      </p>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="rounded border border-border-subtle px-3 py-1 text-xs text-text-primary hover:bg-app-bg"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => submitEdit(shift)}
                        disabled={actions.update.isPending}
                        className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 text-sm font-medium text-text-primary">
                        <span className="truncate">{operatorName}</span>
                        {active && (
                          <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                            De plantão agora
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-text-secondary">
                        {formatRange(shift.startsAt, shift.endsAt)}
                      </p>
                      {shift.note !== null && shift.note !== '' && (
                        <p className="mt-0.5 text-xs italic text-text-secondary">{shift.note}</p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(shift)}
                          className="text-xs text-accent hover:underline"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => removeShift(shift)}
                          disabled={actions.remove.isPending}
                          className="text-xs text-red-600 hover:underline disabled:opacity-50"
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
